// @flow

import type { RouterHistory } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import core from 'app-client/core.coffee';

const DEFAULT_OPTIONS = {
  replace: false,
  changeReloadMarker: false,
  trigger: true,
};

type LoadPageOptions = {
  queryVals?: { [string]: string },
};

const objectToUrlParams = (data: { [string]: string }) =>
  Object.entries(data)
    // $FlowFixMe - Object.entries is still broken
    .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
    .join('&');

export default class BackboneRouter {
  prefix = '/app/';
  history: ?RouterHistory = null;
  simpleURL = false; // This would be true if the app loaded is /bk or /ta in mainapp
  unbindHistoryEventListener = null;
  renderCount = 1; // counts the number of re-renders/router history entries for the current view
  lastPath = '';

  constructor() {
    this.buildUrls();
  }

  setHistory(history: RouterHistory) {
    this.history = history;
    this.bindHistoryEventListener();
  }

  // Binds an event to the router history to track keep track of the number of backbone re-renders for a single view.
  // This allows us to count the number of history records we need to pop when a user uses the history.back() functionality.
  // This event will fire on every route transition and isn't ideal, but we have agreed is the best solution for maintaining history.back()
  bindHistoryEventListener() {
    if (this.unbindHistoryEventListener) {
      this.unbindHistoryEventListener();
    }
    if (this.history) {
      this.unbindHistoryEventListener = this.history.listen(location => {
        if (location.pathname === this.lastPath) {
          this.renderCount++;
        } else {
          this.renderCount = 1;
        }
        this.lastPath = location.pathname;
      });
    }
  }

  navigate(uri: string, options: $Shape<typeof DEFAULT_OPTIONS> = DEFAULT_OPTIONS) {
    const parsedUri = this.parseUri(uri);

    if (!this.history) {
      throw new Error('Router history not set');
    }

    if (options.trigger === false) {
      throw new Error('navigate with options.trigger: false is not supported');
    }

    // Not using this.history.location because of https://reacttraining.com/react-router/web/api/history/history-is-mutable
    // Note that the state object lives as `window.history.state.state` and it will have a
    // reloadMarker value after the first call to navigate()
    const { state = {} } = window.history.state || {};

    const newState = { reloadMarker: 0, ...state };

    if (options.changeReloadMarker) {
      newState.reloadMarker++;
    }

    if (options.replace) {
      this.history.replace(parsedUri, newState);
      return;
    }

    this.history.push(parsedUri, newState);
  }

  loadPage(
    id: string,
    params: Array<string> = [],
    options: LoadPageOptions = {},
    title: string,
    replaceUrl: boolean = false,
  ) {
    const url = this.buildUrl(id, params, options);
    this.navigate(url, { replace: replaceUrl });

    // TODO: Handle old folders (might be needed)

    /**
     * TODO: Handle angular routes.
     * Previously, we created angular page views when user routed to pages
     * created in rind. We might not need this since routing is now handled by
     * react router.
     */

    /** TODO: handle analytics
     * In mainapp, we appended the used id to the document title.
     */
  }

  refreshPage(...args: Array<any>) {
    if (args.length > 0) {
      throw new Error('refreshPage() with param overrides is not supported - use loadPage instead');
    }
    // Not using this.history.location because of https://reacttraining.com/react-router/web/api/history/history-is-mutable
    const { pathname, search, hash } = window.location;
    this.navigate(`${pathname}${search}${hash}`, {
      replace: true,
      changeReloadMarker: true,
    });
  }

  back() {
    if (!this.history) {
      throw new Error('Router history not set');
    }
    this.history.go(-this.renderCount);
  }

  parseLink(href: string) {
    if (!href) {
      return [];
    }

    if (this.simpleURL) {
      return href.replace(this.prefix, '').split('/');
    }

    return href.replace(`${this.prefix}${core.getCurrentClientId()}/`, '').split('/');
  }

  extractPageUrl() {
    const { href } = window.location;
    let regexp;

    if (this.simpleURL) {
      regexp = new RegExp(`${this.prefix.replace(/\//g, '')}/?(.*)`);
    } else {
      regexp = new RegExp(`${this.prefix.replace(/\//g, '')}/[0-9]+/?(.*)`);
    }

    const page = regexp.exec(href);
    if (page && page.length === 2) {
      return page[1];
    }

    return '';
  }

  // Might change depending on Backbone navigate implementation
  parseUri(uri: string) {
    const noHastagUri = uri.replace('#', '');

    if (noHastagUri.charAt(0) !== '/') {
      return `/${noHastagUri}`;
    }

    return noHastagUri;
  }

  buildUrl(id: string, params: Array<string>, options: LoadPageOptions = {}) {
    let url = `${this.prefix}${core.getCurrentClientId()}/${id}`;

    if (params && params.length > 0) {
      url += `/${params.join('/')}`;
    }

    if (options.queryVals) {
      url += `?${objectToUrlParams(options.queryVals)}`;
    }
    return url;
  }

  buildUrls() {
    // $FlowFixMe
    $(document).on('click', '.router-url', e => {
      e.preventDefault();

      const link = $(e.currentTarget);

      // TODO: Make sure this is not needed
      // do not load pages from counters during the loading
      // because we might have different side-effects
      // if (link.hasClass('counter-url') && loader.isLoading()) {
      //   return;
      // }

      const url = link.attr('href');
      const params = this.parseLink(url);

      const id = params.shift();
      const title = link.data('title') || link.text();

      this.loadPage(id, params, {}, title);
    });
  }

  getCurrentPageState() {
    return {
      id: this.extractPageUrl().split('/')[0],
    };
  }
}
